import {useEffect} from 'react';
import {QueryClient, useQueryClient} from '@tanstack/react-query';
import {useRealtime} from '@/context/RealtimeContext';
import {playerKeys} from './queries';

export type PlayerRealtimeEvent = {
  type: string;
  workspace_id?: string;
  payload?: {id: string; scope?: string; [key: string]: unknown};
  resource?: {id: string; scope?: string; [key: string]: unknown};
};

export type PlayerRealtimeStatus = 'connecting' | 'connected' | 'disconnected';

const playerEventTypes = new Set([
  'player.created',
  'player.updated',
  'player.deleted',
  'player.availability.updated',
  'player.document.created',
  'player.document.deleted',
  'player.squad.synchronized',
  'player.squad.reviewed',
  'player.watchlist.updated',
]);

const retryBaseMilliseconds = 1000;
const retryMaximumMilliseconds = 30000;
const retryJitterRatio = 0.2;

export function playerRealtimeRetryDelay(attempt: number, jitter: number): number {
  const boundedAttempt = Math.max(0, Math.min(Math.floor(attempt), 5));
  const base = Math.min(retryMaximumMilliseconds, retryBaseMilliseconds * (2 ** boundedAttempt));
  const boundedJitter = Math.max(-1, Math.min(jitter, 1));
  return Math.min(retryMaximumMilliseconds, Math.round(base * (1 + boundedJitter * retryJitterRatio)));
}

export function handlePlayerRealtime(
  queryClient: QueryClient,
  workspaceId: string,
  event: PlayerRealtimeEvent,
): void {
  const resource = event.payload ?? event.resource;
  if (!workspaceId || (event.workspace_id && event.workspace_id !== workspaceId) || !resource?.id) return;
  if (event.type === 'player.club_roster.imported') {
    void queryClient.invalidateQueries({queryKey: [...playerKeys.all(workspaceId), 'squad']});
    void queryClient.invalidateQueries({queryKey: [...playerKeys.all(workspaceId), 'external']});
    void queryClient.invalidateQueries({
      predicate: query => query.queryKey.length === 5
        && query.queryKey[0] === 'v2'
        && query.queryKey[1] === 'players'
        && query.queryKey[2] === workspaceId
        && query.queryKey[3] === 'detail',
    });
    return;
  }
  if (event.type === 'player.wyscout_import.updated') {
    void queryClient.invalidateQueries({queryKey: playerKeys.detail(workspaceId, resource.id), exact: true});
    void queryClient.invalidateQueries({queryKey: playerKeys.wyscoutImport(workspaceId, resource.id)});
    return;
  }
  if (event.type === 'player.wyscout_analytics.updated') {
    void queryClient.invalidateQueries({queryKey: playerKeys.detail(workspaceId, resource.id), exact: true});
    void queryClient.invalidateQueries({queryKey: [...playerKeys.detail(workspaceId, resource.id), 'wyscout-analytics']});
    return;
  }
  if (!playerEventTypes.has(event.type)) return;
  void queryClient.invalidateQueries({queryKey: playerKeys.all(workspaceId)});
  if (event.type === 'player.watchlist.updated') {
    void queryClient.invalidateQueries({
      queryKey: playerKeys.detail(workspaceId, resource.id),
      exact: true,
    });
    return;
  }
  if (event.type === 'player.squad.synchronized' || event.type === 'player.squad.reviewed') {
    void queryClient.invalidateQueries({queryKey: playerKeys.latestSquadSync(workspaceId)});
    void queryClient.invalidateQueries({queryKey: playerKeys.squadReview(workspaceId)});
    return;
  }
  void queryClient.invalidateQueries({queryKey: playerKeys.detail(workspaceId, resource.id)});
}

export function usePlayerRealtime(workspaceId: string): {status: PlayerRealtimeStatus} {
  const queryClient = useQueryClient();
  const {subscribe, status} = useRealtime();
  useEffect(() => workspaceId ? subscribe(event => handlePlayerRealtime(queryClient, workspaceId, event as PlayerRealtimeEvent)) : undefined, [queryClient, subscribe, workspaceId]);
  return {status};
}
